import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import { getPublicConfig } from "@/api/config";
import type { PublicConfig } from "@/types";

/**
 * Clé TanStack Query stable pour `GET /api/v1/config`.
 * Exposée pour permettre aux tests / consumers d'invalider la query.
 */
const PUBLIC_CONFIG_QUERY_KEY = ["public-config"] as const;

/**
 * Hook S7-3 (R-13 cond. 5) — expose la configuration publique du serveur
 * (notamment `consent_version` pour la modale RGPD).
 *
 * `staleTime` long (10 min) + pas de retry : la valeur change uniquement
 * à chaque release, inutile de la rafraîchir agressivement. Pas de cookie
 * requis (endpoint public).
 *
 * Les consumers qui ont besoin de la version (`ConsentModal`,
 * `PrivacyPolicy`) doivent gérer le cas `isLoading` / `data === undefined` :
 * en pratique, l'appel est rapide (DB-less) mais il faut un fallback safe
 * pendant le premier paint pour éviter d'envoyer un POST avec une version
 * vide.
 */
export function usePublicConfig(): UseQueryResult<PublicConfig, unknown> {
  return useQuery<PublicConfig, unknown>({
    queryKey: PUBLIC_CONFIG_QUERY_KEY,
    queryFn: getPublicConfig,
    staleTime: 10 * 60_000,
    retry: false,
  });
}
